/**
 * useUserPreferences
 *
 * Loads the user's saved filter state on mount and auto-saves any changes
 * (debounced) via /api/user/preferences. The latest state IS the saved state —
 * there is no explicit save action.
 *
 * A payload identical to what the server already holds (including the state
 * just hydrated from it) is not re-sent. A save still waiting on the debounce
 * when the hook unmounts is sent immediately rather than dropped. A failure
 * that repeats is logged once, not on every change.
 *
 * Silent failure: if the API errors, `savedPrefs` is null and the dashboard
 * operates with default empty state. Errors never break the UI.
 *
 * Only fetches/saves when `isAuthenticated` is true.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import apiService from '../services/apiService';
import logger from '../utils/logger';

const AUTO_SAVE_DEBOUNCE_MS = 500;

/**
 * Serialise a preferences payload with filter keys sorted, so the same state
 * compares equal however its object was assembled (hydration spreads saved
 * values over defaults, which changes key order but not meaning).
 */
export const serializePrefs = (filters, anomalyChips) => {
    const source = filters || {};
    const sortedFilters = Object.keys(source).sort().reduce((acc, key) => {
        acc[key] = source[key];
        return acc;
    }, {});
    return JSON.stringify({ filters: sortedFilters, anomalyChips: anomalyChips || [] });
};

const errorKey = (err) => {
    if (!err) return 'no-result';
    const status = err.status ?? err.response?.status ?? '';
    return `${status}:${err.message || String(err)}`;
};

/**
 * @param {boolean} isAuthenticated - Pass `isAuthenticated` from useAuth().
 * @returns {{
 *   hasLoadedPrefs: boolean,
 *   savedPrefs: { filters: object, anomalyChips: string[] } | null,
 *   queueSave: (filters: object, anomalyChips: string[]) => void
 * }}
 */
export default function useUserPreferences(isAuthenticated) {
    const [hasLoadedPrefs, setHasLoadedPrefs] = useState(false);
    const [savedPrefs, setSavedPrefs] = useState(null);
    const saveTimerRef = useRef(null);
    // What the server is known to hold. A payload that serialises to this is
    // not sent — which covers the dashboard re-emitting just-hydrated prefs.
    const lastSavedJsonRef = useRef(null);
    // The save waiting on the debounce: { filters, anomalyChips, json }.
    const pendingRef = useRef(null);
    // Payload of the save currently on its way to the server. Until it returns,
    // the server is about to hold it, so new changes compare against it (D-1).
    const inFlightJsonRef = useRef(null);
    // Key of the last failure logged; a repeat of it stays quiet.
    const lastErrorKeyRef = useRef(null);
    const mountedRef = useRef(true);

    // Fetch on mount (only when authenticated)
    useEffect(() => {
        if (!isAuthenticated) return;

        let cancelled = false;

        (async () => {
            try {
                const data = await apiService.getUserPreferences();
                if (cancelled) return;

                if (data && data.filters) {
                    const anomalyChips = Array.isArray(data.anomalyChips) ? data.anomalyChips : [];
                    lastSavedJsonRef.current = serializePrefs(data.filters, anomalyChips);
                    setSavedPrefs({ filters: data.filters, anomalyChips });
                } else {
                    setSavedPrefs(null);
                }
            } catch (err) {
                if (cancelled) return;
                logger.warn('useUserPreferences: could not load prefs, proceeding with defaults', err);
                setSavedPrefs(null);
            } finally {
                if (!cancelled) setHasLoadedPrefs(true);
            }
        })();

        return () => { cancelled = true; };
    }, [isAuthenticated]);

    const reportFailure = useCallback((err) => {
        const key = errorKey(err);
        if (key === lastErrorKeyRef.current) return;
        lastErrorKeyRef.current = key;
        if (err) {
            logger.error('useUserPreferences: auto-save failed', err);
        } else {
            logger.warn('useUserPreferences: auto-save returned null');
        }
    }, []);

    /** Send the pending save now, if there is one. */
    const flush = useCallback(async () => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (!pending) return;

        const { filters, anomalyChips, json } = pending;
        inFlightJsonRef.current = json;
        try {
            const result = await apiService.saveUserPreferences(filters, anomalyChips);
            if (result) {
                lastSavedJsonRef.current = json;
                lastErrorKeyRef.current = null;
                if (mountedRef.current) setSavedPrefs({ filters, anomalyChips });
            } else {
                reportFailure(null);
            }
        } catch (err) {
            reportFailure(err);
        } finally {
            if (inFlightJsonRef.current === json) inFlightJsonRef.current = null;
        }
    }, [reportFailure]);

    // On unmount, send a pending save rather than dropping it — otherwise a
    // change made just before navigating away is lost.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            flush();
        };
    }, [flush]);

    /**
     * Queue a debounced auto-save. Coalesces rapid changes into a single
     * write after AUTO_SAVE_DEBOUNCE_MS of inactivity, and skips the write
     * entirely when the payload matches what the server already holds.
     */
    const queueSave = useCallback((filters, anomalyChips) => {
        const json = serializePrefs(filters, anomalyChips);
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        // Compare with what the server will hold: the save in flight, if any,
        // else the last confirmed one. Comparing only with the last confirmed
        // save dropped a revert made while the previous save was in flight.
        const expected = inFlightJsonRef.current ?? lastSavedJsonRef.current;
        if (json === expected) {
            pendingRef.current = null;
            return;
        }
        pendingRef.current = { filters, anomalyChips, json };
        saveTimerRef.current = setTimeout(flush, AUTO_SAVE_DEBOUNCE_MS);
    }, [flush]);

    return { hasLoadedPrefs, savedPrefs, queueSave };
}
